import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import type {
	NavigationAufgabenstellungDTO,
	NavigationSachverhaltDTO,
} from "@/types/klausurEditor.types";
import { DestructiveDialog } from "@/components/destructive-dialog.component";
import { useKlausurEditing } from "@/features/klausur/context/klausurEditingProvider";

/**
 * Type for the target of the destructive dialog
 */
type DeleteTarget =
	| {
			type: "aufgabe";
			aufgabenstellungId: string;
			title: string;
	  }
	| {
			type: "sachverhalt";
			aufgabenstellungId: string;
			sachverhaltId: string;
			title: string;
	  }
	| null;

interface KlausurErstellerSidebarActionsContextValue {
	openDeleteAufgabe: (a: NavigationAufgabenstellungDTO) => void;
	openDeleteSachverhalt: (
		aufgabenstellungId: string,
		s: NavigationSachverhaltDTO,
	) => void;
}

/**
 * Context for the KlausurErstellerSidebarActionsProvider
 * - provides actions for the sidebar
 */
const KlausurErstellerSidebarActionsContext = createContext<
	KlausurErstellerSidebarActionsContextValue | undefined
>(undefined);

/**
 * Provider for the KlausurErstellerSidebarActionsProvider
 * - provides actions for the sidebar
 */
export function KlausurErstellerSidebarActionsProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const { deleteAufgabenstellung, deleteSachverhalt } = useKlausurEditing();
	const [isOpen, setIsOpen] = useState(false);
	const [target, setTarget] = useState<DeleteTarget>(null);

	const openDeleteAufgabe = useCallback((a: NavigationAufgabenstellungDTO) => {
		setTarget({
			type: "aufgabe",
			aufgabenstellungId: a.id,
			title: a.title || "Ohne Titel",
		});
		setIsOpen(true);
	}, []);

	const openDeleteSachverhalt = useCallback(
		(aufgabenstellungId: string, s: NavigationSachverhaltDTO) => {
			setTarget({
				type: "sachverhalt",
				aufgabenstellungId,
				sachverhaltId: s.id,
				title: s.title || "Ohne Titel",
			});
			setIsOpen(true);
		},
		[],
	);

	const onCancel = useCallback(() => {
		setIsOpen(false);
		setTarget(null);
	}, []);

	const onConfirm = useCallback(async () => {
		if (!target) return;
		try {
			if (target.type === "aufgabe") {
				await deleteAufgabenstellung(target.aufgabenstellungId);
			} else {
				await deleteSachverhalt(
					target.aufgabenstellungId,
					target.sachverhaltId,
				);
			}
		} finally {
			onCancel();
		}
	}, [target, deleteAufgabenstellung, deleteSachverhalt, onCancel]);

	const value = useMemo(
		() => ({ openDeleteAufgabe, openDeleteSachverhalt }),
		[openDeleteAufgabe, openDeleteSachverhalt],
	);

	return (
		<KlausurErstellerSidebarActionsContext.Provider value={value}>
			{children}
			<DestructiveDialog
				isOpen={isOpen}
				title={
					target?.type === "aufgabe"
						? "Aufgabenstellung löschen"
						: "Sachverhalt löschen"
				}
				description={
					target?.type === "aufgabe"
						? "Möchten Sie diese Aufgabenstellung wirklich löschen?"
						: "Möchten Sie diesen Sachverhalt wirklich löschen?"
				}
				warning={
					target?.type === "aufgabe"
						? "Dies löscht auch alle zugehörigen Sachverhalte und Musterlösungen."
						: undefined
				}
				details={
					target ? (
						<div>
							<p className="text-sm">Titel:</p>
							<p className="font-medium">{target.title}</p>
						</div>
					) : null
				}
				onConfirm={onConfirm}
				onCancel={onCancel}
				confirmLabel="Löschen"
			/>
		</KlausurErstellerSidebarActionsContext.Provider>
	);
}

/**
 * Hook to use the KlausurErstellerSidebarActionsContext
 */
export function useKlausurErstellerSidebarActions() {
	const ctx = useContext(KlausurErstellerSidebarActionsContext);
	if (!ctx) {
		throw new Error(
			"useKlausurErstellerSidebarActions must be used within KlausurErstellerSidebarActionsProvider",
		);
	}
	return ctx;
}
